import type { ReactElement } from 'react';
import { BLOG_ROLES, type BlogRole, type MemberDto } from '@gr/core/accounts/workspaces/dto';
import { useT } from '../../../../app/di';
import { formatDate } from '../../../../shared/format';
import { Button } from '../../../../ui-kit/Button/Button';
import { DataTable } from '../../../../ui-kit/DataTable/DataTable';
import { BareSelect } from '../../../../ui-kit/Field/Field';

export interface MemberTableProps {
  members: readonly MemberDto[];
  /** Владелец может менять роли и убирать участников (WS-UC-5). */
  manage: boolean;
  pending: boolean;
  onRoleChange(member: MemberDto, role: BlogRole): void;
  onRemove(member: MemberDto): void;
}

/** WS-UC-4, WS-UC-5: участники блога с ролями. */
export function MemberTable({ members, manage, pending, onRoleChange, onRemove }: MemberTableProps): ReactElement {
  const { t, locale } = useT();
  const roleOptions = BLOG_ROLES.map((value) => ({ value, label: t(`workspaces.roles.${value}`) }));
  return (
    <DataTable
      label={t('workspaces.members.title')}
      rows={members}
      rowKey={(member) => member.userId}
      columns={[
        {
          key: 'user',
          header: t('workspaces.members.user'),
          primary: true,
          cell: (member) => (
            <>
              <strong>{member.displayName}</strong>
              <div className="u-muted u-small">{member.login}</div>
            </>
          ),
        },
        {
          key: 'role',
          header: t('workspaces.members.role'),
          cell: (member) =>
            manage ? (
              <BareSelect label={t('workspaces.members.role')} value={member.role} options={roleOptions} disabled={pending} onChange={(e) => onRoleChange(member, e.target.value as BlogRole)} />
            ) : (
              t(`workspaces.roles.${member.role}`)
            ),
        },
        { key: 'since', header: t('workspaces.members.since'), cell: (member) => <span className="u-muted">{formatDate(member.joinedAt, locale)}</span> },
        {
          key: 'actions',
          header: '',
          actions: true,
          cell: (member) =>
            manage ? (
              <Button size="sm" variant="ghost" icon="trash" disabled={pending} onClick={() => onRemove(member)}>
                {t('workspaces.members.remove')}
              </Button>
            ) : null,
        },
      ]}
    />
  );
}
